import mergeWith from 'lodash.mergewith';
import flatten from 'lodash.flatten';

import { CARD_ELEMENT_TYPE, ORIENTATION } from '../../constants';

/**
 * Returns sizing information about a CardTemplate, depending on the ORIENTATION.
 * When LANDSCAPE the largest dimension is returned as the width, and when PORTRAIT the largest
 * dimension is returned as the height.
 *
 * @param {Object} cardTemplate The CardTemplate.
 * @param {Object} defaults The width/height defaults.
 * @returns {{width: number, height: number, ratio: number}}
 */
export const getCardTemplateSizing = (
  cardTemplate,
  defaults = {
    width: 7.25,
    height: 5.25,
  },
) => {
  const defaultWidth = defaults.width;
  const defaultHeight = defaults.height;

  if (!cardTemplate) {
    return {
      width: defaultWidth,
      height: defaultHeight,
      ratio: defaultHeight / defaultWidth,
    };
  }

  const orientation = cardTemplate.orientation ?? ORIENTATION.LANDSCAPE;
  const { width = defaultWidth, height = defaultHeight } =
    cardTemplate.print_product?.print_size ?? cardTemplate.print_size ?? {};
  const canonicalWidth =
    orientation === ORIENTATION.LANDSCAPE
      ? Math.max(width, height)
      : Math.min(width, height);
  const canonicalHeight =
    orientation === ORIENTATION.LANDSCAPE
      ? Math.min(width, height)
      : Math.max(width, height);

  return {
    width: canonicalWidth,
    height: canonicalHeight,
    ratio: canonicalHeight / canonicalWidth,
  };
};

/**
 * Creates the AccountCard UI state.
 *
 * @param {Object} accountCard The AccountCard.
 * @param {Object} [cardElementLoading] The CardElement that is currently loading (create/update).
 * @param {Boolean} linkImageElements Wether or not to link image elements
 * @returns {Array}
 */
export const getAccountCardElementsConfiguration = (
  accountCard,
  cardElementLoading,
  linkImageElements = false,
) => {
  if (!accountCard || !accountCard?.account_card_sides) {
    return [];
  }
  // How to extract the AccountCard UI state:
  //
  // 1. For each AccountCardSide of the AccountCard get its CardElements.
  // 2. Loop through each CardElement (on both AccountCardSides) and find their CardElementConfigurations.
  //    (CardElements can have multiple CardElementConfigurations, the default one and the user saved one).
  // 4. Merge all CardElementConfigurations in the following order: default -> user (if it exists).
  // 5. Return a flat list of CardElements with their configurations for consumption by the UI.
  const accountCardSidesConfigurationStates =
    accountCard.account_card_sides.map(accountCardSide => {
      const cardSide = accountCardSide.card_side;
      const cardElements = cardSide.card_template.card_elements;
      const defaultCardElementConfigurations =
        cardSide.card_element_configurations;
      const userSavedCardElementConfigurations =
        accountCardSide.card_element_configurations;

      return cardElements.map(cardElement => {
        const defaultCardElementConfiguration =
          defaultCardElementConfigurations.find(
            x => x.card_element.id === cardElement.id,
          );
        const userSavedCardElementConfiguration =
          userSavedCardElementConfigurations.find(
            x => x.card_element.id === cardElement.id,
          );

        return {
          cardElement,
          defaultCardElementConfiguration,
          cardElementConfiguration: mergeWith(
            {},
            defaultCardElementConfiguration,
            userSavedCardElementConfiguration,
          ),
          side: accountCardSide.side,
          loading:
            cardElementLoading?.id === cardElement.id ||
            (linkImageElements &&
              cardElementLoading?.type === CARD_ELEMENT_TYPE.PHOTO &&
              cardElement?.type === CARD_ELEMENT_TYPE.PHOTO),
        };
      });
    });

  return flatten(accountCardSidesConfigurationStates);
};

/**
 * Determines whether a CardElement is editable.
 *
 * @param {Object} [element] The CardElement with CardElementConfiguration.
 * @returns {boolean}
 */
export const isCardElementEditable = (element = {}) => {
  const { cardElementConfiguration } = element;

  if (
    !cardElementConfiguration ||
    cardElementConfiguration?.is_editable === false
  ) {
    return false;
  }

  return true;
};

/**
 * Determines if the cardElement's frame fills the full width
 *
 * @param {Object} frame The CardElement's frame
 * @returns {boolean}
 */
export const isFullFrame = (frame = {}) => {
  return (
    frame.x === 0 && frame.y === 0 && frame.width === 1 && frame.height === 1
  );
};

/**
 * Determines if there are pending labeled text elements missing filling onthe current side
 *
 * @param {CARD_SIDE} side The current card element side
 * @param {Array} elements The account card elements
 *
 * @returns {Boolean}
 */

export const hasPendingLabeledTextElementsOnSide = (side, elements) => {
  if (elements?.length === 0 || !elements) {
    return false;
  }

  return !!elements.find(
    element =>
      element.side === side &&
      element.cardElement.type === CARD_ELEMENT_TYPE.TEXT &&
      !!element.defaultCardElementConfiguration?.label &&
      !element.cardElementConfiguration?.text,
  );
};
